import type { AdAccountsConfirmation, AdPlatform, StepState } from '@/contracts/onboarding-session';

import { useAdDiscoveryEvents } from '../hooks/useAdDiscoveryEvents';
import { useAdAnalysis } from '../hooks/useOnboardingQueries';
import type { RecoverSession } from '../hooks/useSessionRecovery';
import { AdsCard } from './AdsCard';

/**
 * What the BFF filters the platform's ads by: every ad until the confirmation, and after a skip; only the
 * confirmed accounts' ads after a pick. The feed is keyed on this, so a confirmation makes it read from the start.
 */
export function adConfirmationKey(confirmation: AdAccountsConfirmation | undefined): string {
  return !confirmation || confirmation.skipped ? 'all' : `only:${[...confirmation.accountIds].sort().join(',')}`;
}

export interface AdsFeedProps {
  sessionId: string;
  platform: AdPlatform;
  /** Brings the BFF session back when the feed finds it gone. */
  recover?: RecoverSession;
  /** The brand's name, the site the ads link to, and the snapshot's state of this platform's discovery job (for the card's title, source line and badge). */
  brand?: string | null;
  site?: string;
  jobState?: StepState;
}

/** The ads card of one platform, owning that platform's event and analysis polling (mounted only once the card is shown). */
export function AdsFeed({ sessionId, platform, recover, brand, site, jobState }: AdsFeedProps) {
  const state = useAdDiscoveryEvents({ sessionId, platform, enabled: true, recover });
  const analysis = useAdAnalysis(sessionId, platform, true, recover);
  return <AdsCard platform={platform} state={state} analysis={analysis} brand={brand} site={site} jobState={jobState} />;
}
